import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateUserInput,
  ProjectUser,
  Role,
  RoleChangeInput,
  StubReplaceInput,
} from '@testflow/shared';
import { ROLES, SCOPED_ROLES } from '@testflow/shared';
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Combobox,
  ComboboxList,
  ComboboxNotice,
  ComboboxOption,
} from '../../../components/combobox.js';
import { ConfirmDialog } from '../../../components/confirm-dialog.js';
import { ApiError } from '../../../lib/api.js';
import { mapApiError } from '../../../lib/errors.js';
import { useScopedUser } from '../../../lib/project-roles.js';
import { type StructureNode, structureApi } from '../../../lib/structure-api.js';
import { useCurrentProject } from '../../../lib/use-project.js';
import { usersApi } from '../../../lib/users-api.js';

// Task 9 + 10: Nutzerverwaltung je Projekt — Tabelle der Rollen-Träger + "Ohne Rolle"-Sektion für
// Nutzer, die sich bereits eingeloggt, aber noch keine Rolle bekommen haben (AUTH-Fluss), plus das
// Anlegen-Modal und der Rolle-Zuweisungs-Dialog. Seit #112 (A11) trägt jede Rollenzuweisung
// zusätzlich ihren Struktur-Knoten (`scope_node_id`); Vorgabe bleibt projektweit.

const fieldClass =
  'w-full rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1.5 text-sm text-text-body';

// Rollen-Select, gemeinsam genutzt vom Anlegen-Modal und dem Rolle-Zuweisungs-Dialog. Die
// admin-Option wird für Nicht-Admins ausgegraut (nur ein Admin darf die Admin-Rolle vergeben).
function RoleSelect({
  id,
  value,
  onChange,
  isAdmin,
  includeEmptyOption,
}: {
  id: string;
  value: Role | '';
  onChange: (value: Role | '') => void;
  isAdmin: boolean;
  includeEmptyOption?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <select
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value as Role | '')}
      className={fieldClass}
    >
      {includeEmptyOption && <option value="">{t('admin.users.form.noRole')}</option>}
      {ROLES.map((role) => {
        const locked = role === 'admin' && !isAdmin;
        return (
          <option
            key={role}
            value={role}
            disabled={locked}
            title={locked ? t('admin.users.adminRoleLocked') : undefined}
          >
            {t(`admin.users.roleNames.${role}`)}
          </option>
        );
      })}
    </select>
  );
}

// #112 (A11): Auswahl des Struktur-Knotens, auf den eine Rolle gescopt wird. `scope_node_id` war
// nie ein totes Feld — `can()` wertet es fail-closed gegen den Knoten-Kontext aus
// (packages/shared/src/permissions.ts), Sign-off und Reports ebenso; es fehlte nur die Pflege.
// Bis hierher stand an allen Schreibstellen fest `scope_node_id: null`, wodurch sich vor allem die
// Rolle „Verantwortlicher" keinem Teilprojekt/Stream zuweisen ließ.
// Vorgabe bleibt projektweit (`null`). Die Knotenart steht im Label mit, weil ein Name allein
// „Vertrieb (Teilprojekt)" nicht von „Order to Cash (Stream)" unterscheidbar macht.
//
// Das Feld erscheint nur fuer scope-faehige Rollen (SCOPED_ROLES = `verantwortlicher`). Fuer alle
// anderen — allen voran den Consultant — waere es ein Feld ohne Konsequenz: `can()` liest den
// Knoten dort nicht, die Berechtigungsmatrix (requirements.md §5) kennt fuer sie keinen Scope.
// Der Server weist so einen Body zusaetzlich ab (RoleGrantWriteSchema).
function ScopeSelect({
  id,
  value,
  onChange,
  nodes,
}: {
  id: string;
  value: string | null;
  onChange: (value: string | null) => void;
  nodes: StructureNode[];
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-1">
      <label className="block text-xs text-text-muted" htmlFor={id}>
        {t('admin.users.form.scope')}
      </label>
      <select
        id={id}
        value={value ?? ''}
        // Leerer Wert = projektweit. Der Server prüft zusätzlich, dass ein gewählter Knoten zu
        // diesem Projekt gehört (assertScopeInProject in routes/users.ts).
        onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
        className={fieldClass}
      >
        <option value="">{t('admin.users.form.scopeAll')}</option>
        {nodes.map((n) => (
          <option key={n.node_id} value={n.node_id}>
            {`${n.name} (${t(`admin.structure.kindLabel.${n.kind}`)})`}
          </option>
        ))}
      </select>
    </div>
  );
}

// Beschriftung eines Rollen-Chips: „Vertrieb (Teilprojekt)" bzw. „Projektweit". Die Route liefert
// zwar `scope_node_name` mit (Join auf structureNodes), aber nicht die Knotenart — die kommt aus
// der ohnehin geladenen Knotenliste; fehlt sie (noch), bleibt der Name allein stehen.
function scopeLabel(
  scopeNodeId: string | null,
  scopeNodeName: string | null,
  nodes: StructureNode[],
  t: (key: string) => string,
): string {
  if (!scopeNodeId) return t('admin.users.form.scopeAll');
  const node = nodes.find((n) => n.node_id === scopeNodeId);
  const name = node?.name ?? scopeNodeName ?? scopeNodeId;
  return node ? `${name} (${t(`admin.structure.kindLabel.${node.kind}`)})` : name;
}

function CreateUserModal({
  isAdmin,
  isPending,
  nodes,
  onCancel,
  onSubmit,
}: {
  isAdmin: boolean;
  isPending: boolean;
  nodes: StructureNode[];
  onCancel: () => void;
  onSubmit: (input: CreateUserInput) => void;
}) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isExternal, setIsExternal] = useState(false);
  const [role, setRole] = useState<Role | ''>('');
  const [scopeNodeId, setScopeNodeId] = useState<string | null>(null);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !name.trim()) return;
    onSubmit({
      email: email.trim(),
      name: name.trim(),
      is_external: isExternal,
      // #112 (A11): Der gewählte Knoten geht mit in die Anfangsrolle. Ohne Rolle gibt es auch
      // keinen Scope — das Feld bleibt dann folgenlos.
      ...(role ? { initial_role: { role, scope_node_id: scopeNodeId } } : {}),
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <form
        onSubmit={submit}
        className="w-96 space-y-3 rounded-card border border-[var(--border)] bg-bg-surface p-4 shadow-lg"
      >
        <h3 className="text-sm font-bold text-text-primary">{t('admin.users.newUser')}</h3>
        <div className="space-y-1">
          <label className="block text-xs text-text-muted" htmlFor="new-user-email">
            {t('admin.users.form.email')}
          </label>
          <input
            id="new-user-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={fieldClass}
          />
        </div>
        <div className="space-y-1">
          <label className="block text-xs text-text-muted" htmlFor="new-user-name">
            {t('admin.users.form.name')}
          </label>
          <input
            id="new-user-name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={fieldClass}
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-text-body">
          <input
            type="checkbox"
            checked={isExternal}
            onChange={(e) => setIsExternal(e.target.checked)}
          />
          {t('admin.users.form.external')}
        </label>
        <div className="space-y-1">
          <label className="block text-xs text-text-muted" htmlFor="new-user-role">
            {t('admin.users.form.role')}
          </label>
          <RoleSelect
            id="new-user-role"
            value={role}
            onChange={(v) => {
              setRole(v);
              // Rollenwechsel raeumt einen Knoten weg, den die neue Rolle nicht tragen kann —
              // sonst schickt das Formular ein `scope_node_id`, das der Server abweist
              // (RoleGrantWriteSchema: scope_not_supported_for_role).
              if (!v || !SCOPED_ROLES.includes(v)) setScopeNodeId(null);
            }}
            isAdmin={isAdmin}
            includeEmptyOption
          />
        </div>
        {role && SCOPED_ROLES.includes(role) ? (
          <ScopeSelect
            id="new-user-scope"
            value={scopeNodeId}
            onChange={setScopeNodeId}
            nodes={nodes}
          />
        ) : null}
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-btn px-3 py-1.5 text-sm text-text-muted hover:text-text-body"
          >
            {t('admin.users.form.cancel')}
          </button>
          <button
            type="submit"
            disabled={isPending}
            className="rounded-btn bg-brand-accent px-3 py-1.5 text-sm text-white disabled:opacity-60"
          >
            {t('admin.users.form.submit')}
          </button>
        </div>
      </form>
    </div>
  );
}

function RoleAssignDialog({
  userName,
  isAdmin,
  isPending,
  titleKey,
  nodes,
  onCancel,
  onSubmit,
}: {
  userName: string;
  isAdmin: boolean;
  isPending: boolean;
  titleKey: 'assignRole' | 'addRole';
  nodes: StructureNode[];
  onCancel: () => void;
  onSubmit: (role: Role, scopeNodeId: string | null) => void;
}) {
  const { t } = useTranslation();
  const [role, setRole] = useState<Role>('tester');
  // #112 (A11): Vorgabe projektweit — der Knoten wird bewusst gewählt, nicht geraten.
  const [scopeNodeId, setScopeNodeId] = useState<string | null>(null);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    onSubmit(role, scopeNodeId);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <form
        onSubmit={submit}
        className="w-96 space-y-3 rounded-card border border-[var(--border)] bg-bg-surface p-4 shadow-lg"
      >
        <h3 className="text-sm font-bold text-text-primary">
          {t(`admin.users.${titleKey}`)} · {userName}
        </h3>
        <div className="space-y-1">
          <label className="block text-xs text-text-muted" htmlFor="role-dialog-role">
            {t('admin.users.form.role')}
          </label>
          <RoleSelect
            id="role-dialog-role"
            value={role}
            onChange={(v) => {
              if (!v) return;
              setRole(v);
              if (!SCOPED_ROLES.includes(v)) setScopeNodeId(null);
            }}
            isAdmin={isAdmin}
          />
        </div>
        {SCOPED_ROLES.includes(role) ? (
          <ScopeSelect
            id="role-dialog-scope"
            value={scopeNodeId}
            onChange={setScopeNodeId}
            nodes={nodes}
          />
        ) : null}
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-btn px-3 py-1.5 text-sm text-text-muted hover:text-text-body"
          >
            {t('admin.users.form.cancel')}
          </button>
          <button
            type="submit"
            disabled={isPending}
            className="rounded-btn bg-brand-accent px-3 py-1.5 text-sm text-white disabled:opacity-60"
          >
            {t(`admin.users.${titleKey}`)}
          </button>
        </div>
      </form>
    </div>
  );
}

const STUB_EMAIL_SUFFIX = '@stub.invalid';

// Der Server liefert beim Ersetzen eines Platzhalters mit bereits vergebener Adresse 409 mit dem
// Plain-Code `email_already_exists` (routes/users.ts, HTTPException).
function isEmailTaken(err: unknown): boolean {
  return err instanceof ApiError && err.status === 409 && err.message === 'email_already_exists';
}

type StubMode = 'adopt' | 'merge';

// #130: „Platzhalter ersetzen" — zwei Wege in einem Modal. Der frühere Inline-Weg (E-Mail im
// Tabellenfeld umbiegen) ließ den Nutzer inaktiv und ohne Rolle zurück; hier entsteht der echte
// Nutzer in EINEM Schritt mit Rolle (Weg 1), oder alle Zuweisungen wandern auf einen Nutzer, den es
// schon gibt (Weg 2, unumkehrbar → Rückfrage).
// Fehler zeigt das Modal selbst an (errorToast: false an den Mutationen) — der Toast läge sonst
// unter bzw. neben dem Modal, und der 409 `email_already_exists` braucht ohnehin eine eigene
// Darstellung mit Ausweg („Stattdessen zusammenführen").
function ReplaceStubModal({
  stub,
  users,
  isAdmin,
  isPending,
  nodes,
  onCancel,
  onAdopt,
  onMerge,
}: {
  stub: ProjectUser;
  users: ProjectUser[];
  isAdmin: boolean;
  isPending: boolean;
  nodes: StructureNode[];
  onCancel: () => void;
  onAdopt: (input: StubReplaceInput, onError: (err: unknown) => void) => void;
  onMerge: (targetUserId: string, onError: (err: unknown) => void) => void;
}) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<StubMode>('adopt');
  const [name, setName] = useState(stub.name);
  const [email, setEmail] = useState('');
  // Platzhalter stammen aus Importen externer Testfälle — Vorgabe extern, wie im Brief.
  const [isExternal, setIsExternal] = useState(true);
  const [role, setRole] = useState<Role>('tester');
  const [scopeNodeId, setScopeNodeId] = useState<string | null>(null);
  const [emailError, setEmailError] = useState(false);
  const [triedEmail, setTriedEmail] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [query, setQuery] = useState('');
  const [targetId, setTargetId] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const realUsers = users.filter((u) => !u.is_stub);
  const target = realUsers.find((u) => u.user_id === targetId) ?? null;
  const needle = query.trim().toLowerCase();
  const matches = realUsers.filter(
    (u) => needle === '' || `${u.name} ${u.email}`.toLowerCase().includes(needle),
  );
  const userLabel = (u: ProjectUser) => `${u.name} · ${u.email}`;

  // Wem gehört die vergebene Adresse? Abgleich gegen die geladene Liste (Server vergleicht
  // kleingeschrieben). Ist der Besitzer nicht sichtbar (anderes Projekt) oder selbst ein
  // Platzhalter, gibt es keinen Zusammenführen-Ausweg — dann bleibt der übersetzte Fehler.
  const normalizedEmail = email.trim().toLowerCase();
  const emailOwner =
    isEmailTaken(error) && normalizedEmail === triedEmail
      ? (realUsers.find((u) => u.email.toLowerCase() === triedEmail) ?? null)
      : null;
  // 409 für die aktuell eingegebene Adresse, aber der Besitzer steht nicht in der Projektliste
  // (geladen werden nur Nutzer mit Rolle im Projekt): kein Zusammenführen-Angebot möglich —
  // stattdessen den Weg erklären (erst Rolle vergeben).
  const emailTakenWithoutOwner =
    isEmailTaken(error) && normalizedEmail === triedEmail && emailOwner === null;

  // Eine fehlgeschlagene Zusammenführung schließt die Rückfrage, der Fehler steht im Modal.
  const handleError = (err: unknown) => {
    setError(err);
    setConfirmOpen(false);
  };

  const submitAdopt = (e: FormEvent) => {
    e.preventDefault();
    const trimmedEmail = email.trim();
    if (!trimmedEmail || !name.trim()) return;
    // Der Server lehnt Platzhalter-Adressen per Schema-Refine mit einem 400-Zod-Body ab (kein
    // benannter Code) — ohne Client-Sperre käme nur der generische Validierungsfehler an.
    if (trimmedEmail.toLowerCase().endsWith(STUB_EMAIL_SUFFIX)) {
      setEmailError(true);
      return;
    }
    setEmailError(false);
    setError(null);
    setTriedEmail(trimmedEmail.toLowerCase());
    onAdopt(
      {
        email: trimmedEmail,
        name: name.trim(),
        is_external: isExternal,
        role: { role, scope_node_id: scopeNodeId },
      },
      handleError,
    );
  };

  const submitMerge = (e: FormEvent) => {
    e.preventDefault();
    if (!target) return;
    setError(null);
    setConfirmOpen(true);
  };

  const switchToMerge = (owner: ProjectUser | null) => {
    setError(null);
    setMode('merge');
    if (owner) {
      setTargetId(owner.user_id);
      setQuery(userLabel(owner));
    }
  };

  const tabClass = (active: boolean) =>
    `flex-1 rounded-btn px-2 py-1 text-xs ${
      active
        ? 'bg-brand-accent text-white'
        : 'border border-[var(--border)] text-text-body hover:border-brand-accent'
    }`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <form
        onSubmit={mode === 'adopt' ? submitAdopt : submitMerge}
        aria-label={t('admin.users.replaceStub.title', { name: stub.name })}
        className="w-[28rem] max-w-full space-y-3 rounded-card border border-[var(--border)] bg-bg-surface p-4 shadow-lg"
      >
        <h3 className="text-sm font-bold text-text-primary">
          {t('admin.users.replaceStub.title', { name: stub.name })}
        </h3>
        <div className="flex gap-2">
          <button
            type="button"
            aria-pressed={mode === 'adopt'}
            onClick={() => {
              setError(null);
              setMode('adopt');
            }}
            className={tabClass(mode === 'adopt')}
          >
            {t('admin.users.replaceStub.modeAdopt')}
          </button>
          <button
            type="button"
            aria-pressed={mode === 'merge'}
            onClick={() => switchToMerge(null)}
            className={tabClass(mode === 'merge')}
          >
            {t('admin.users.replaceStub.modeMerge')}
          </button>
        </div>

        {mode === 'adopt' ? (
          <>
            <p className="text-xs text-text-muted">{t('admin.users.replaceStub.adoptHint')}</p>
            <div className="space-y-1">
              <label className="block text-xs text-text-muted" htmlFor="replace-stub-name">
                {t('admin.users.form.name')}
              </label>
              <input
                id="replace-stub-name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={fieldClass}
              />
            </div>
            <div className="space-y-1">
              <label className="block text-xs text-text-muted" htmlFor="replace-stub-email">
                {t('admin.users.form.email')}
              </label>
              <input
                id="replace-stub-email"
                type="email"
                required
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setEmailError(false);
                  // Ein alter Fehler (z. B. 409 "E-Mail vergeben") gilt für die vorherige Adresse.
                  setError(null);
                }}
                aria-invalid={emailError}
                className={fieldClass}
              />
              {emailError && (
                <div className="text-xs text-status-fail">{t('errors.stub_email_not_allowed')}</div>
              )}
            </div>
            <label className="flex items-center gap-2 text-sm text-text-body">
              <input
                type="checkbox"
                checked={isExternal}
                onChange={(e) => setIsExternal(e.target.checked)}
              />
              {t('admin.users.form.external')}
            </label>
            <div className="space-y-1">
              <label className="block text-xs text-text-muted" htmlFor="replace-stub-role">
                {t('admin.users.form.role')}
              </label>
              <RoleSelect
                id="replace-stub-role"
                value={role}
                onChange={(v) => {
                  if (!v) return;
                  setRole(v);
                  if (!SCOPED_ROLES.includes(v)) setScopeNodeId(null);
                }}
                isAdmin={isAdmin}
              />
            </div>
            {SCOPED_ROLES.includes(role) ? (
              <ScopeSelect
                id="replace-stub-scope"
                value={scopeNodeId}
                onChange={setScopeNodeId}
                nodes={nodes}
              />
            ) : null}
          </>
        ) : (
          <>
            <p className="text-xs text-text-muted">{t('admin.users.replaceStub.mergeHint')}</p>
            <div className="space-y-1">
              <Combobox
                label={t('admin.users.replaceStub.mergeTarget')}
                placeholder={t('admin.users.replaceStub.mergePlaceholder')}
                value={query}
                onChange={(v) => {
                  setQuery(v);
                  // Tippen hebt eine bereits getroffene Auswahl auf — gesendet wird nur, was
                  // sichtbar ausgewählt ist.
                  setTargetId(null);
                }}
                className={fieldClass}
              >
                {({ open, close, listboxId }) =>
                  open ? (
                    <ComboboxList id={listboxId}>
                      {matches.length === 0 ? (
                        <ComboboxNotice>{t('admin.users.replaceStub.noMatches')}</ComboboxNotice>
                      ) : (
                        matches.map((u) => (
                          <ComboboxOption
                            key={u.user_id}
                            selected={u.user_id === targetId}
                            onSelect={() => {
                              setTargetId(u.user_id);
                              setQuery(userLabel(u));
                              close();
                            }}
                          >
                            {userLabel(u)}
                          </ComboboxOption>
                        ))
                      )}
                    </ComboboxList>
                  ) : null
                }
              </Combobox>
            </div>
          </>
        )}

        {emailOwner ? (
          <div role="alert" className="space-y-1 text-xs text-status-fail">
            <div>{t('admin.users.replaceStub.emailTaken', { name: emailOwner.name })}</div>
            <button
              type="button"
              onClick={() => switchToMerge(emailOwner)}
              className="rounded-btn border border-[var(--border)] px-2 py-1 text-text-primary hover:border-brand-accent"
            >
              {t('admin.users.replaceStub.mergeInstead')}
            </button>
          </div>
        ) : error ? (
          <div role="alert" className="space-y-1 text-xs text-status-fail">
            <div>{mapApiError(t, error)}</div>
            {emailTakenWithoutOwner && (
              <div className="text-text-muted">{t('admin.users.replaceStub.emailTakenNoRole')}</div>
            )}
          </div>
        ) : null}

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-btn px-3 py-1.5 text-sm text-text-muted hover:text-text-body"
          >
            {t('admin.users.form.cancel')}
          </button>
          <button
            type="submit"
            disabled={isPending || (mode === 'merge' && !target)}
            className="rounded-btn bg-brand-accent px-3 py-1.5 text-sm text-white disabled:opacity-60"
          >
            {mode === 'adopt'
              ? t('admin.users.replaceStub.submitAdopt')
              : t('admin.users.replaceStub.submitMerge')}
          </button>
        </div>
      </form>

      {confirmOpen && target && (
        <ConfirmDialog
          title={t('admin.users.replaceStub.confirmTitle')}
          message={t('admin.users.replaceStub.confirmMerge', {
            stub: stub.name,
            target: target.name,
          })}
          confirmLabel={t('admin.users.replaceStub.submitMerge')}
          danger
          pending={isPending}
          onCancel={() => setConfirmOpen(false)}
          onConfirm={() => onMerge(target.user_id, handleError)}
        />
      )}
    </div>
  );
}

// Task 13: E-Mail/Name inline pflegbar — u.a. für Stub-User aus dem Import (Task 5), die mit einer
// Platzhalter-Mail (…@stub.invalid) angelegt werden. Muster wie StructureRow (admin/structure-panel):
// eigener lokaler Draft je Feld, Speichern-Button nur aktiv wenn etwas geändert wurde. Ein
// useEffect hält den Draft nach einem Refetch (z.B. nach dem Speichern) mit dem Server synchron.
function UserRow({
  u,
  isAdmin,
  isUpdating,
  nodes,
  onUpdate,
  onReactivate,
  onDeactivateRequest,
  onRemoveRoleRequest,
  onAddRole,
  onReplaceStub,
}: {
  u: ProjectUser;
  isAdmin: boolean;
  isUpdating: boolean;
  nodes: StructureNode[];
  onUpdate: (userId: string, patch: { email?: string; name?: string }) => void;
  onReactivate: (userId: string) => void;
  onDeactivateRequest: (userId: string, name: string) => void;
  onRemoveRoleRequest: (
    userId: string,
    role: Role,
    scopeNodeId: string | null,
    roleLabel: string,
    userName: string,
  ) => void;
  onAddRole: (userId: string, name: string) => void;
  onReplaceStub: (u: ProjectUser) => void;
}) {
  const { t } = useTranslation();
  const [email, setEmail] = useState(u.email);
  const [name, setName] = useState(u.name);

  useEffect(() => {
    setEmail(u.email);
    setName(u.name);
  }, [u.email, u.name]);

  const trimmedEmail = email.trim();
  const trimmedName = name.trim();
  const emailDirty = trimmedEmail !== '' && trimmedEmail !== u.email;
  const nameDirty = trimmedName !== '' && trimmedName !== u.name;
  const dirty = emailDirty || nameDirty;

  const save = () => {
    if (!dirty) return;
    const patch: { email?: string; name?: string } = {};
    if (emailDirty) patch.email = trimmedEmail;
    if (nameDirty) patch.name = trimmedName;
    onUpdate(u.user_id, patch);
  };

  return (
    <tr
      data-testid={`user-row-${u.user_id}`}
      className="border-b border-[var(--border)] last:border-0"
    >
      <td className="px-3 py-2 text-text-primary">
        <div className="flex items-center gap-2">
          <input
            aria-label={t('admin.users.form.name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={fieldClass}
          />
          {u.is_stub && (
            <span className="whitespace-nowrap rounded-chip border border-status-warn bg-status-warn/10 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-text-primary">
              {t('admin.users.stubBadge')}
            </span>
          )}
        </div>
      </td>
      <td className="px-3 py-2 text-text-body">
        {/* #130: Bei Platzhaltern gesperrt — der Inline-Weg ließ den Nutzer inaktiv und ohne Rolle
            zurück; die E-Mail kommt jetzt über „Platzhalter ersetzen" (mit Rolle). */}
        <input
          aria-label={t('admin.users.form.email')}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={u.is_stub}
          title={u.is_stub ? t('admin.users.replaceStub.emailLocked') : undefined}
          className={`${fieldClass} disabled:cursor-not-allowed disabled:opacity-60`}
        />
      </td>
      <td className="px-3 py-2 text-text-muted">
        {u.is_external ? t('admin.users.kindExternal') : t('admin.users.kindInternal')}
      </td>
      <td className="px-3 py-2">
        <div className="flex flex-wrap items-center gap-1">
          {u.roles.map((r) => {
            // Nicht-Admins dürfen die admin-Rolle nicht entziehen (Backend blockt mit
            // 403 — hier zusätzlich UX-konsistent zur RoleSelect-Ausgrauung sperren).
            const removeLocked = r.role === 'admin' && !isAdmin;
            const roleLabel = t(`admin.users.roleNames.${r.role}`);
            // #112 (A11): Worauf eine Rolle gescopt ist, muss am Chip ablesbar sein. Sichtbar
            // steht nur ein gewählter Knoten — „Projektweit" an jeden Chip zu hängen würde die
            // Zeile bei mehreren Rollen sprengen (Zielschirm 1280px); der title nennt den
            // Scope in jedem Fall ausgeschrieben.
            const scope = scopeLabel(r.scope_node_id, r.scope_node_name, nodes, t);
            return (
              <span
                key={r.user_role_id}
                title={`${roleLabel} · ${scope}`}
                className="inline-flex items-center gap-1 rounded-chip bg-brand-accent/10 px-2 py-0.5 text-xs text-brand-accent"
              >
                {roleLabel}
                {r.scope_node_id ? ` · ${scope}` : ''}
                <button
                  type="button"
                  disabled={removeLocked}
                  onClick={() =>
                    onRemoveRoleRequest(
                      u.user_id,
                      r.role as Role,
                      r.scope_node_id,
                      roleLabel,
                      u.name,
                    )
                  }
                  aria-label={t('admin.users.removeRole')}
                  title={
                    removeLocked ? t('admin.users.adminRoleLocked') : t('admin.users.removeRole')
                  }
                  className="text-brand-accent/70 hover:text-status-fail disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:text-brand-accent/70"
                >
                  ✕
                </button>
              </span>
            );
          })}
          <button
            type="button"
            onClick={() => onAddRole(u.user_id, u.name)}
            className="rounded-btn px-1.5 py-0.5 text-xs text-brand-accent hover:bg-brand-accent/10"
          >
            + {t('admin.users.addRole')}
          </button>
        </div>
      </td>
      <td className="px-3 py-2">
        <span className={u.is_active ? 'text-status-pass' : 'text-text-muted'}>
          {u.is_active ? t('admin.users.active') : t('admin.users.inactive')}
        </span>
      </td>
      <td className="px-3 py-2 text-right">
        <div className="flex flex-wrap items-center justify-end gap-2">
          {u.is_stub && (
            <button
              type="button"
              onClick={() => onReplaceStub(u)}
              className="whitespace-nowrap rounded-btn bg-brand-accent px-2 py-1 text-xs text-white"
            >
              {t('admin.users.replaceStub.button')}
            </button>
          )}
          <button
            type="button"
            onClick={save}
            disabled={!dirty || isUpdating}
            className="rounded-btn border border-[var(--border)] px-2 py-1 text-xs text-text-primary hover:border-brand-accent disabled:opacity-50"
          >
            {t('admin.users.save')}
          </button>
          <button
            type="button"
            onClick={() =>
              u.is_active ? onDeactivateRequest(u.user_id, u.name) : onReactivate(u.user_id)
            }
            className="rounded-btn px-2 py-1 text-xs text-text-muted hover:text-text-primary"
          >
            {u.is_active ? t('admin.users.deactivate') : t('admin.users.reactivate')}
          </button>
        </div>
      </td>
    </tr>
  );
}

export function UsersPanel() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { project, isLoading: projectLoading } = useCurrentProject();
  const { user } = useScopedUser();
  const projectId = project?.project_id;
  const [hideInactive, setHideInactive] = useState(false);
  const [onlyStubs, setOnlyStubs] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [roleDialogUser, setRoleDialogUser] = useState<{
    userId: string;
    name: string;
    mode: 'assignRole' | 'addRole';
  } | null>(null);
  // #96: Rückfragen vor den beiden irreversiblen Aktionen dieses Panels. Reaktivieren bleibt
  // ohne Rückfrage (Nutzerentscheidung im Brief) — nur Deaktivieren und Rolle-Entziehen fragen.
  const [confirmRemoveRole, setConfirmRemoveRole] = useState<{
    userId: string;
    role: Role;
    scopeNodeId: string | null;
    roleLabel: string;
    userName: string;
  } | null>(null);
  const [confirmDeactivate, setConfirmDeactivate] = useState<{
    userId: string;
    name: string;
  } | null>(null);
  // #130: Platzhalter, dessen „ersetzen"-Modal offen ist (ganze Zeile, damit Name im Titel steht).
  const [replaceStubUser, setReplaceStubUser] = useState<ProjectUser | null>(null);

  // #23: `admin` in DIESEM Projekt (lib/project-roles.ts). Ungescopt blendete die Rollenauswahl
  // „Admin vergeben" auch dort ein, wo der Server die Vergabe abweist.
  const isAdmin = user?.roles.some((r) => r.role === 'admin') ?? false;

  const usersQuery = useQuery({
    queryKey: ['project-users', projectId],
    queryFn: () => usersApi.list(projectId ?? ''),
    enabled: Boolean(projectId),
  });
  const unassignedQuery = useQuery({
    queryKey: ['project-users-unassigned', projectId],
    queryFn: () => usersApi.listUnassigned(projectId ?? ''),
    enabled: Boolean(projectId),
  });
  // #112 (A11): Knotenliste für den Scope-Select. Gleicher Query-Key wie im Struktur-Panel,
  // damit sich beide Panels denselben Cache-Eintrag teilen.
  const nodesQuery = useQuery({
    queryKey: ['structure-nodes', projectId],
    queryFn: () => structureApi.list(projectId ?? ''),
    enabled: Boolean(projectId),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['project-users', projectId] });
    void queryClient.invalidateQueries({ queryKey: ['project-users-unassigned', projectId] });
  };
  const setActive = useMutation({
    mutationFn: ({ userId, active }: { userId: string; active: boolean }) =>
      usersApi.setActive(projectId ?? '', userId, active),
    meta: { successToast: 'feedback.saved' },
    onSuccess: () => {
      invalidate();
      setConfirmDeactivate(null);
    },
  });
  const createUser = useMutation({
    mutationFn: (input: CreateUserInput) => usersApi.create(projectId ?? '', input),
    meta: { successToast: 'feedback.created' },
    onSuccess: () => {
      invalidate();
      setShowCreate(false);
    },
  });
  const changeRoles = useMutation({
    mutationFn: ({ userId, change }: { userId: string; change: RoleChangeInput }) =>
      usersApi.changeRoles(projectId ?? '', userId, change),
    meta: { successToast: 'feedback.saved' },
    onSuccess: () => {
      invalidate();
      setRoleDialogUser(null);
      setConfirmRemoveRole(null);
    },
  });
  // Task 13: E-Mail/Name pflegen — u.a. Stub-User (Import, Task 5) auf eine echte Mail umbiegen.
  // Der Server räumt is_stub selbst ab; hier reicht Invalidate, damit der Badge nach dem Refetch
  // verschwindet (kein Extra-Client-Zustand nötig).
  const updateUser = useMutation({
    mutationFn: ({
      userId,
      patch,
    }: {
      userId: string;
      patch: { email?: string; name?: string };
    }) => usersApi.updateUser(projectId ?? '', userId, patch),
    meta: { successToast: 'feedback.saved' },
    onSuccess: invalidate,
  });
  // #130: Platzhalter ersetzen. Fehler zeigt das Modal selbst (errorToast: false) — der 409
  // `email_already_exists` bekommt dort einen Ausweg („Stattdessen zusammenführen").
  const replaceStub = useMutation({
    mutationFn: ({ userId, input }: { userId: string; input: StubReplaceInput }) =>
      usersApi.replaceStub(projectId ?? '', userId, input),
    meta: { successToast: 'feedback.saved', errorToast: false },
    onSuccess: () => {
      invalidate();
      setReplaceStubUser(null);
    },
  });
  const mergeStub = useMutation({
    mutationFn: ({ userId, targetUserId }: { userId: string; targetUserId: string }) =>
      usersApi.mergeInto(projectId ?? '', userId, { target_user_id: targetUserId }),
    meta: { successToast: 'feedback.merged', errorToast: false },
    onSuccess: () => {
      invalidate();
      setReplaceStubUser(null);
    },
  });

  if (projectLoading) return <div className="p-6 text-text-muted">{t('common.loading')}</div>;
  if (!project || !projectId)
    return <div className="p-6 text-text-muted">{t('catalog.noProject')}</div>;

  const rows = (usersQuery.data ?? []).filter(
    (u) => (!hideInactive || u.is_active) && (!onlyStubs || u.is_stub),
  );
  const nodes = nodesQuery.data ?? [];

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-text-primary">{t('admin.users.title')}</h2>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-text-body">
            <input
              type="checkbox"
              checked={hideInactive}
              onChange={(e) => setHideInactive(e.target.checked)}
            />
            {t('admin.users.hideInactive')}
          </label>
          <label className="flex items-center gap-2 text-sm text-text-body">
            <input
              type="checkbox"
              checked={onlyStubs}
              onChange={(e) => setOnlyStubs(e.target.checked)}
            />
            {t('admin.users.onlyStubs')}
          </label>
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="rounded-btn bg-brand-accent px-3 py-1.5 text-sm text-white"
          >
            {t('admin.users.newUser')}
          </button>
        </div>
      </div>

      <section className="rounded-card border border-[var(--border)] bg-bg-surface">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--border)] text-left text-[11px] font-bold uppercase text-text-muted">
              <th className="px-3 py-2">{t('admin.users.columns.name')}</th>
              <th className="px-3 py-2">{t('admin.users.columns.email')}</th>
              <th className="px-3 py-2">{t('admin.users.columns.kind')}</th>
              <th className="px-3 py-2">{t('admin.users.columns.roles')}</th>
              <th className="px-3 py-2">{t('admin.users.columns.status')}</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {rows.map((u) => (
              <UserRow
                key={u.user_id}
                u={u}
                isAdmin={isAdmin}
                isUpdating={updateUser.isPending}
                nodes={nodes}
                onUpdate={(userId, patch) => updateUser.mutate({ userId, patch })}
                onReactivate={(userId) => setActive.mutate({ userId, active: true })}
                onDeactivateRequest={(userId, name) => setConfirmDeactivate({ userId, name })}
                onRemoveRoleRequest={(userId, role, scopeNodeId, roleLabel, userName) =>
                  setConfirmRemoveRole({ userId, role, scopeNodeId, roleLabel, userName })
                }
                onAddRole={(userId, name) => setRoleDialogUser({ userId, name, mode: 'addRole' })}
                onReplaceStub={setReplaceStubUser}
              />
            ))}
          </tbody>
        </table>
        {rows.length === 0 && (
          <div className="p-4 text-sm text-text-muted">{t('admin.users.empty')}</div>
        )}
      </section>

      <section className="space-y-2">
        <h3 className="text-sm font-bold text-text-primary">{t('admin.users.unassignedTitle')}</h3>
        {(unassignedQuery.data ?? []).length === 0 && (
          <div className="text-sm text-text-muted">{t('admin.users.unassignedEmpty')}</div>
        )}
        {(unassignedQuery.data ?? []).map((u) => (
          <div
            key={u.user_id}
            className="flex items-center justify-between rounded-card border border-[var(--border)] bg-bg-surface px-3 py-2 text-sm"
          >
            <span>
              {u.name} · <span className="text-text-muted">{u.email}</span>
            </span>
            <button
              type="button"
              onClick={() =>
                setRoleDialogUser({ userId: u.user_id, name: u.name, mode: 'assignRole' })
              }
              className="rounded-btn bg-brand-accent px-2 py-1 text-xs text-white"
            >
              {t('admin.users.assignRole')}
            </button>
          </div>
        ))}
      </section>

      {showCreate && (
        <CreateUserModal
          isAdmin={isAdmin}
          isPending={createUser.isPending}
          nodes={nodes}
          onCancel={() => setShowCreate(false)}
          onSubmit={(input) => createUser.mutate(input)}
        />
      )}

      {replaceStubUser && (
        <ReplaceStubModal
          stub={replaceStubUser}
          users={usersQuery.data ?? []}
          isAdmin={isAdmin}
          isPending={replaceStub.isPending || mergeStub.isPending}
          nodes={nodes}
          onCancel={() => setReplaceStubUser(null)}
          onAdopt={(input, onError) =>
            replaceStub.mutate({ userId: replaceStubUser.user_id, input }, { onError })
          }
          onMerge={(targetUserId, onError) =>
            mergeStub.mutate({ userId: replaceStubUser.user_id, targetUserId }, { onError })
          }
        />
      )}

      {roleDialogUser && (
        <RoleAssignDialog
          userName={roleDialogUser.name}
          isAdmin={isAdmin}
          isPending={changeRoles.isPending}
          titleKey={roleDialogUser.mode}
          nodes={nodes}
          onCancel={() => setRoleDialogUser(null)}
          // #112 (A11): Der im Dialog gewählte Knoten wird mitgeschickt statt fest null.
          onSubmit={(role, scopeNodeId) =>
            changeRoles.mutate({
              userId: roleDialogUser.userId,
              change: { add: { role, scope_node_id: scopeNodeId } },
            })
          }
        />
      )}

      {confirmRemoveRole && (
        <ConfirmDialog
          title={t('admin.users.removeRole')}
          message={t('admin.users.confirmRemoveRole', {
            role: confirmRemoveRole.roleLabel,
            name: confirmRemoveRole.userName,
          })}
          confirmLabel={t('admin.users.removeRole')}
          danger
          pending={changeRoles.isPending}
          onCancel={() => setConfirmRemoveRole(null)}
          onConfirm={() =>
            changeRoles.mutate({
              userId: confirmRemoveRole.userId,
              change: {
                remove: {
                  role: confirmRemoveRole.role,
                  scope_node_id: confirmRemoveRole.scopeNodeId,
                },
              },
            })
          }
        />
      )}

      {confirmDeactivate && (
        <ConfirmDialog
          title={t('admin.users.deactivate')}
          message={t('admin.users.confirmDeactivate', { name: confirmDeactivate.name })}
          confirmLabel={t('admin.users.deactivate')}
          danger
          pending={setActive.isPending}
          onCancel={() => setConfirmDeactivate(null)}
          onConfirm={() => setActive.mutate({ userId: confirmDeactivate.userId, active: false })}
        />
      )}
    </div>
  );
}
